import { OrderResultsReducerChangeLastOrderAction, OrderResultsReducerNewOrderPendingAction } from 'components/features/rewards/rewardsPane/store/actions/ordersResults'
import { EMPTY, merge, Observable, of } from 'rxjs'
import { mergeMap } from 'rxjs/operators'

import { OrderResultWithPendingStatus, OrderFailedResult, OrderSuccessResultWithMeta } from 'components/features/rewards/rewardsPane/types/orderResult'
import {
  CRYPTO_REWARDS_BUY_MODE_STEP_INVESTMENTS_AMOUNT,
  CRYPTO_REWARDS_BUY_MODE_STEP_SEND_ORDER,
  CRYPTO_REWARDS_BUY_MODE_STEP_SUCCESS,
} from 'components/features/rewards/rewardsPane/constants/paneSteps'
import { EpicRefined } from 'interfaces/middleware'
import { ajaxCatch, AjaxCatchExtraActions } from 'rxOperators/ajaxCatch'
import { CryptoRewardsPaneActions } from 'components/features/rewards/rewardsPane/store/actions'
import {
  BuyCryptoAction,
  createRewardsPaneActionChangeBuyModeStepState,
  SuccessCryptoRewardsPaneModeStepPayload,
} from 'components/features/rewards/rewardsPane/store/actions/paneState'
import { FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { POST_ORDER_BUY_CRYPTO_URL } from 'components/features/rewards/rewardsPane/constants/endpoints'
import { ModifyFloatingPane } from 'actions/floatingPanesActions'
import { CryptoRewardsBuyCryptoOrderRequest } from 'components/features/rewards/rewardsPane/types/apiRequests'
import { CRYPTO_CURRENCY_INVESTMENT_MODE_AUTO } from 'components/features/rewards/rewardsPane/constants/investmentMode'
import { SetRewardSummaryAction } from 'actions/bountyActions'
import { CryptoRewardsBuyCryptoOrderSuccessResponse, CryptoRewardsValidationErrorsResponse } from 'components/features/rewards/rewardsPane/types/apiResponses'
import { CRYPTO_REWARDS_PANE_GENERAL_UNKNOWN_RESPONSE_ERROR } from 'components/features/rewards/rewardsPane/constants/defaults'
import size from 'lodash/size'
import { mapOrderDetailsAPIResponseFormatToReducerFormat } from 'components/features/rewards/rewardsPane/store/reducers/ordersResults'
import { filterRequest } from 'rxOperators/filterRequestOfType'
import { currentPageSelector } from 'selectors/globalSelectors'
import { activateAnchorActionCreator, pushActionCreator } from 'actions/navigationActions'
import { REWARDS_PAGE } from 'constants/pages'
import { CURRENCY_NOT_SUPPORTED } from 'constants/errors'
import { CurrenciesMetaGetMarketDataRequestAction } from 'components/features/rewards/rewardsPane/store/actions/currenciesStatistics'

type PaneBuyMode = typeof FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE

export const buyCryptoCreateOrderEpic: EpicRefined<CryptoRewardsPaneActions | ModifyFloatingPane | SetRewardSummaryAction | AjaxCatchExtraActions> = (
  (action$, store, { post }) => action$
    .pipe(
      filterRequest(BuyCryptoAction.type),
      mergeMap(
        (action) => {
          const { payload } = action
          if (payload.params.step !== CRYPTO_REWARDS_BUY_MODE_STEP_INVESTMENTS_AMOUNT) {
            return EMPTY
          }

          const { fields }: SuccessCryptoRewardsPaneModeStepPayload<PaneBuyMode> = payload.params

          if (!fields) {
            throw new Error('No order data')
          }

          const {
            cryptoCurrencyInvestmentsModeChosen,
            cryptoCurrencyIDChosenForInvestment,
            investmentsAmountUSD,
          } = fields

          const actionOpenSendingOrderStep: ModifyFloatingPane = (
            createRewardsPaneActionChangeBuyModeStepState({
              step: CRYPTO_REWARDS_BUY_MODE_STEP_SEND_ORDER,
              progressPercents: 70,
              fields: {
                ...fields,
              },
            })
          )
          const actionOpenSendingOrderStepObservable: Observable<ModifyFloatingPane> = of(actionOpenSendingOrderStep)
          const actionResetLastOrderResultObservable = of(new OrderResultsReducerNewOrderPendingAction(FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE))

          const sendOrderParams: CryptoRewardsBuyCryptoOrderRequest = {
            enable_autoinvest: cryptoCurrencyInvestmentsModeChosen === CRYPTO_CURRENCY_INVESTMENT_MODE_AUTO,
            amount_usd: investmentsAmountUSD,
            ticker: cryptoCurrencyIDChosenForInvestment,
          }
          const sendOrderObservable = post(POST_ORDER_BUY_CRYPTO_URL, {
            body: sendOrderParams,
          }).pipe(
            mergeMap(({ response }) => {
              const {
                order,
                reward_account,
              }: CryptoRewardsBuyCryptoOrderSuccessResponse = response

              const orderSuccessResult: OrderSuccessResultWithMeta<PaneBuyMode> = mapOrderDetailsAPIResponseFormatToReducerFormat(order)
              const orderResultWithStatus: OrderResultWithPendingStatus<PaneBuyMode> = {
                mode: FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE,
                isPending: false,
                success: orderSuccessResult,
                errors: undefined,
              }
              const actionSetOrderResult = new OrderResultsReducerChangeLastOrderAction(orderResultWithStatus)
              const actionChangeUserRewardsState = new SetRewardSummaryAction(reward_account)
              const actionChangePaneStep = createRewardsPaneActionChangeBuyModeStepState({
                step: CRYPTO_REWARDS_BUY_MODE_STEP_SUCCESS,
                progressPercents: 100,
                fields: {
                  ...fields,
                },
              })


              const currentPage = currentPageSelector(store.getState(), { location: window.location })
              const isRewardsPage = currentPage === 'rewards'
              const redirectAction = isRewardsPage ? EMPTY : merge(
                of(pushActionCreator(null)),
                of(pushActionCreator(REWARDS_PAGE)),
                of(activateAnchorActionCreator(REWARDS_PAGE)),
              )

              return merge(
                of(actionSetOrderResult),
                of(actionChangeUserRewardsState),
                of(actionChangePaneStep),
                of(action.success(response)),
                redirectAction,
              )
            }),
            ajaxCatch((error) => {
              const {
                amount_usd,
                ticker,
                enable_autoinvest,
                non_field_errors,
              }: CryptoRewardsValidationErrorsResponse<PaneBuyMode> = Boolean(size(error.response))
                ? error.response
                : CRYPTO_REWARDS_PANE_GENERAL_UNKNOWN_RESPONSE_ERROR
              const fieldsErrors = {
                amount_usd,
                ticker,
                enable_autoinvest,
              }
              const orderFailedErrors: OrderFailedResult<PaneBuyMode> = {
                fieldsErrors: fieldsErrors,
                generalErrors: non_field_errors,
              }
              const orderResultWithStatus: OrderResultWithPendingStatus<PaneBuyMode> = {
                mode: FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE,
                isPending: false,
                errors: orderFailedErrors,
                success: undefined,
              }
              const actionSetOrderResult = new OrderResultsReducerChangeLastOrderAction(orderResultWithStatus)
              const actionChangePaneStep = createRewardsPaneActionChangeBuyModeStepState({
                step: CRYPTO_REWARDS_BUY_MODE_STEP_INVESTMENTS_AMOUNT,
                progressPercents: 20,
                fields: {
                  ...fields,
                },
              })

              const reloadMarketData$ = (() => {
                if (!non_field_errors || non_field_errors[0] !== CURRENCY_NOT_SUPPORTED) {
                  return EMPTY
                }

                return of(new CurrenciesMetaGetMarketDataRequestAction())
              })()

              return merge(
                of(actionSetOrderResult),
                of(actionChangePaneStep),
                of(action.fail({ errorCode: error.status, errorsMap: error.response })),
                reloadMarketData$,
              )
            })
          )
          return merge(
            actionOpenSendingOrderStepObservable,
            actionResetLastOrderResultObservable,
            sendOrderObservable
          )
        }
      )
    )
)
